/**
 * HR Organization Dashboard
 *
 * Organization-wide statistics and insights for HR.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useEvaluations } from '../evaluations/useEvaluations';
import { useEmployees } from '../employees/useEmployees';
import type { EvaluationStatus } from '../../lib/evaluationsApi';

// XXtract-palet (bindend, FR-23, Epic 5 Story 5.1): geel/blauw/paars-tegels
// verdwijnen. Statuskleur-conventie: final=groen (voltooid), submitted/
// in_review=teal-tinten (informatief/in behandeling), draft=tekstkleur
// (neutraal, nog niet ingediend), cancelled=rood (terminale niet-doorgang,
// analoog aan "afgewezen" in de verlof-conventie).
const STATUS_COLORS: Record<EvaluationStatus, string> = {
  draft: '#1E293B',
  submitted: '#54949E',
  in_review: '#2F5A7A',
  final: '#B7D945',
  cancelled: '#D64545',
};

export function HROrganizationDashboard() {
  const { data: evaluations, isLoading: isLoadingEvals } = useEvaluations();
  const { data: employees, isLoading: isLoadingEmps } = useEmployees();

  const stats = useMemo(() => {
    const evals = evaluations?.data || [];
    const emps = employees?.data || [];

    // Status distribution
    const statusCounts: Record<EvaluationStatus, number> = {
      draft: 0,
      submitted: 0,
      in_review: 0,
      final: 0,
      cancelled: 0,
    };
    evals.forEach((e) => {
      statusCounts[e.status]++;
    });

    // Score statistics
    const finalEvals = evals.filter((e) => e.status === 'final' && e.totalScore);
    const scores = finalEvals.map((e) => parseFloat(e.totalScore || '0'));
    const avgScore = scores.length > 0
      ? scores.reduce((a, b) => a + b, 0) / scores.length
      : 0;
    const maxScore = scores.length > 0 ? Math.max(...scores) : 0;
    const minScore = scores.length > 0 ? Math.min(...scores) : 0;

    // Score distribution (1-5)
    const scoreDistribution: number[] = [0, 0, 0, 0, 0];
    scores.forEach((score) => {
      const bucket = Math.max(0, Math.min(4, Math.floor(score) - 1));
      if (scoreDistribution[bucket] !== undefined) {
        scoreDistribution[bucket]++;
      }
    });

    // Employees with evaluations
    const employeesWithEvals = new Set(evals.map((e) => e.employeeId));

    // Pending actions
    const pendingSubmitted = evals.filter((e) => e.status === 'submitted').length;
    const pendingInReview = evals.filter((e) => e.status === 'in_review').length;

    return {
      totalEmployees: emps.length,
      totalEvaluations: evals.length,
      employeesWithEvals: employeesWithEvals.size,
      statusCounts,
      avgScore,
      maxScore,
      minScore,
      scoreDistribution,
      pendingSubmitted,
      pendingInReview,
      completionRate: emps.length > 0
        ? (employeesWithEvals.size / emps.length) * 100
        : 0,
    };
  }, [evaluations?.data, employees?.data]);

  if (isLoadingEvals || isLoadingEmps) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2F5A7A]"></div>
        <span className="ml-3 text-gray-600">Dashboard laden...</span>
      </div>
    );
  }

  return (
    <div data-testid="hr-organization-dashboard" className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Medewerkers"
          value={stats.totalEmployees}
          subtitle={`${stats.employeesWithEvals} met beoordeling`}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          }
          color="navy"
        />
        <StatCard
          title="Beoordelingen"
          value={stats.totalEvaluations}
          subtitle={`${stats.statusCounts.final} afgerond`}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          }
          color="teal"
        />
        <StatCard
          title="Gem. Score"
          value={stats.avgScore.toFixed(2)}
          subtitle={`${stats.minScore.toFixed(1)} - ${stats.maxScore.toFixed(1)} range`}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
          color="green"
        />
        <StatCard
          title="Wacht op actie"
          value={stats.pendingSubmitted + stats.pendingInReview}
          subtitle={`${stats.pendingSubmitted} ingediend, ${stats.pendingInReview} in review`}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          color="teal"
          highlight={stats.pendingSubmitted + stats.pendingInReview > 0}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Status Verdeling</h3>
          <div className="space-y-3">
            {(['final', 'in_review', 'submitted', 'draft', 'cancelled'] as EvaluationStatus[]).map((status) => {
              const count = stats.statusCounts[status];
              const percentage = stats.totalEvaluations > 0
                ? (count / stats.totalEvaluations) * 100
                : 0;

              return (
                <div key={status}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-600 capitalize">
                      {status === 'final' ? 'Definitief' :
                       status === 'in_review' ? 'In Review' :
                       status === 'submitted' ? 'Ingediend' :
                       status === 'draft' ? 'Concept' : 'Geannuleerd'}
                    </span>
                    <span className="font-medium text-gray-900">{count} ({percentage.toFixed(0)}%)</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: STATUS_COLORS[status],
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Score Distribution */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Score Verdeling</h3>
          <div className="flex items-end justify-around gap-2 h-40">
            {stats.scoreDistribution.map((count, index) => {
              const maxCount = Math.max(...stats.scoreDistribution, 1);
              const height = (count / maxCount) * 100;
              const score = index + 1;

              return (
                <div key={index} className="flex flex-col items-center">
                  <span className="text-xs text-gray-500 mb-1">{count}</span>
                  <div
                    // Code-review fix: een lage score (<=2) is een echt
                    // aandachtspunt — rood blijft hier op zijn plaats als
                    // waarschuwingssignaal (consistent met "rood = echte
                    // problemen", niet enkel "afgewezen/foutmelding");
                    // neutraal navy verhulde dit signaal onterecht.
                    className={`w-12 rounded-t ${
                      score <= 2 ? 'bg-[#D64545]' :
                      score <= 4 ? 'bg-[#54949E]' :
                      'bg-[#B7D945]'
                    }`}
                    style={{ height: `${Math.max(4, height)}%` }}
                  ></div>
                  <span className="text-sm font-medium text-gray-700 mt-2">{score}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex justify-between text-xs text-gray-500">
            <span>Laag</span>
            <span>Score (1-5)</span>
            <span>Hoog</span>
          </div>
        </div>
      </div>

      {/* Completion Progress */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Cyclus Voortgang</h3>
          <span className="text-2xl font-bold text-[#2F5A7A]">
            {stats.completionRate.toFixed(0)}%
          </span>
        </div>
        <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#2F5A7A] to-[#B7D945] rounded-full transition-all duration-500"
            style={{ width: `${stats.completionRate}%` }}
          ></div>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          {stats.employeesWithEvals} van {stats.totalEmployees} medewerkers hebben minimaal één beoordeling.
        </p>
      </div>

      {/* Quick Actions — XXtract-palet: teal (informatief/actie nodig, geen echte fout) */}
      {(stats.pendingSubmitted > 0 || stats.pendingInReview > 0) && (
        <div className="bg-[#54949E]/10 border border-[#54949E]/30 rounded-lg p-6">
          <h3 className="text-lg font-medium text-[#1E293B] mb-4">Acties Vereist</h3>
          <div className="flex flex-wrap gap-4">
            {stats.pendingSubmitted > 0 && (
              <Link
                to="/hr?status=submitted"
                className="flex items-center gap-2 px-4 py-2 bg-white border border-[#54949E]/40 rounded-lg text-[#54949E] hover:bg-[#54949E]/10"
              >
                <span className="flex items-center justify-center w-6 h-6 bg-[#54949E] text-white rounded-full text-sm font-bold">
                  {stats.pendingSubmitted}
                </span>
                <span>Ingediende beoordelingen reviewen</span>
              </Link>
            )}
            {stats.pendingInReview > 0 && (
              <Link
                to="/hr?status=in_review"
                className="flex items-center gap-2 px-4 py-2 bg-white border border-[#2F5A7A]/40 rounded-lg text-[#2F5A7A] hover:bg-[#2F5A7A]/10"
              >
                <span className="flex items-center justify-center w-6 h-6 bg-[#2F5A7A] text-white rounded-full text-sm font-bold">
                  {stats.pendingInReview}
                </span>
                <span>Beoordelingen accorderen</span>
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
  // XXtract-palet (bindend, FR-23, Epic 5 Story 5.1): 'blue'/'purple'/'yellow'
  // vervangen door 'navy'/'teal'/'green' — geen felblauw/paars/geel-tegels meer.
  color: 'navy' | 'teal' | 'green';
  highlight?: boolean;
}

function StatCard({ title, value, subtitle, icon, color, highlight }: StatCardProps) {
  const colorClasses = {
    navy: 'bg-[#2F5A7A]/10 border-[#2F5A7A]/30 text-[#2F5A7A]',
    teal: 'bg-[#54949E]/10 border-[#54949E]/30 text-[#54949E]',
    green: 'bg-[#B7D945]/15 border-[#B7D945]/40 text-[#5C7A00]',
  };

  return (
    <div className={`p-4 rounded-lg border ${colorClasses[color]} ${highlight ? 'ring-2 ring-[#2F5A7A]/60' : ''}`}>
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0">{icon}</div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm font-medium">{title}</p>
          <p className="text-xs opacity-75">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}
